import { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { AppLogoIcon } from '../components/ui/AppLogo';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { SeoHead } from '../components/seo/SeoHead';
import { getLabels } from '../constants/labels';
import {
  DELETE_DATA_URL,
  HELP_URL,
  HELP_PDF_URL,
  PRIVACY_URL,
  TERMS_URL,
  SUPPORT_MAILTO,
  SUPPORT_WHATSAPP_URL,
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_2,
} from '../constants/contact';
import { GITHUB_PROFILE_URL } from '../constants/branding';
const TESTER_URL = 'https://tester.nmhelper.in';

function goToLogin() {
  window.location.assign('/login');
}

function useScrolled(threshold = 32) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

function FeatureIcon({ name }) {
  const common = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  switch (name) {
    case 'edit':
      return (
        <svg {...common}>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      );
    case 'chart':
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 15l4-4 3 3 5-6" />
        </svg>
      );
    case 'wifi':
      return (
        <svg {...common}>
          <path d="M5 12.55a11 11 0 0 1 14 0" />
          <path d="M8.5 16.05a6 6 0 0 1 7 0" />
          <line x1="12" y1="20" x2="12" y2="20" />
          <path d="M2 8.5a16 16 0 0 1 20 0" />
        </svg>
      );
    case 'globe':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18" />
          <path d="M12 3a14 14 0 0 0 0 18" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common}>
          <path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case 'device':
      return (
        <svg {...common}>
          <rect x="6" y="2" width="12" height="20" rx="2" />
          <line x1="12" y1="18" x2="12" y2="18" />
        </svg>
      );
    case 'nutrition':
      return (
        <svg {...common}>
          <path d="M12 3c-2 2-4 5-4 8a4 4 0 0 0 8 0c0-3-2-6-4-8z" />
          <path d="M8 14h8" />
          <path d="M9 18h6" />
        </svg>
      );
    default:
      return null;
  }
}

function NavBar({ labels, useMalayalam }) {
  const scrolled = useScrolled(40);
  const reduceMotion = useReducedMotion();

  const target = scrolled
    ? {
        marginTop: 12,
        width: 'min(880px, calc(100% - 24px))',
        paddingLeft: 14,
        paddingRight: 6,
        paddingTop: 6,
        paddingBottom: 6,
        borderRadius: 9999,
        boxShadow: '0 12px 32px -12px rgba(15,23,42,0.18)',
        backgroundColor: 'rgb(var(--color-card) / 0.92)',
        borderColor: 'rgb(var(--color-border) / 0.7)',
      }
    : {
        marginTop: 0,
        width: '100%',
        paddingLeft: 20,
        paddingRight: 20,
        paddingTop: 14,
        paddingBottom: 14,
        borderRadius: 0,
        boxShadow: '0 0 0 rgba(0,0,0,0)',
        backgroundColor: 'rgb(var(--color-card) / 0.6)',
        borderColor: 'rgb(var(--color-border) / 0.5)',
      };

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center">
      <motion.nav
        initial={false}
        animate={reduceMotion ? undefined : target}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        style={{
          backdropFilter: 'saturate(140%) blur(10px)',
          WebkitBackdropFilter: 'saturate(140%) blur(10px)',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
        className="pointer-events-auto flex items-center justify-between gap-3"
      >
        <a
          href="#top"
          className="flex items-center gap-2 text-text"
          aria-label="NMhelper home"
        >
          <AppLogoIcon className="h-8 w-8 object-contain" />
          <span className="text-base font-semibold tracking-tight">NMhelper</span>
        </a>

        <div className="hidden items-center gap-1 md:flex">
          <a
            href="#features"
            className="rounded-full px-3 py-1.5 text-sm font-medium text-muted hover:bg-primary-light/40 hover:text-primary"
          >
            {labels.landingNavFeatures}
          </a>
          <a
            href="#how"
            className="rounded-full px-3 py-1.5 text-sm font-medium text-muted hover:bg-primary-light/40 hover:text-primary"
          >
            {labels.landingNavHow}
          </a>
          <a
            href="#faq"
            className="rounded-full px-3 py-1.5 text-sm font-medium text-muted hover:bg-primary-light/40 hover:text-primary"
          >
            {labels.landingNavFaq}
          </a>
        </div>

        <div className="flex items-center gap-2">
          <ThemeToggle useMalayalam={useMalayalam} />
          <a
            href={useMalayalam ? '/?lang=en' : '/?lang=ml'}
            className="hidden rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-semibold text-text hover:bg-primary-light/40 sm:inline-flex"
          >
            {labels.toggleLang}
          </a>
          <button
            type="button"
            onClick={goToLogin}
            className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-hover active:scale-[0.98]"
          >
            {labels.landingLoginCta}
          </button>
        </div>
      </motion.nav>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-muted">
      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
      {children}
    </span>
  );
}

function HeroMockup({ labels }) {
  const rows = [
    { c: '1A', b: 22, g: 20 },
    { c: '2A', b: 18, g: 24 },
    { c: '3B', b: 21, g: 19 },
  ];
  return (
    <motion.div
      variants={fadeUp}
      className="relative w-full overflow-hidden rounded-2xl border border-border bg-card shadow-card"
    >
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-text">{labels.landingMockTitle}</p>
          <p className="text-xs text-muted">{labels.landingMockSubtitle}</p>
        </div>
        <div className="flex gap-1.5" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full bg-danger/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-warning/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-success/70" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 px-5 py-4">
        <div className="rounded-xl border border-border bg-surface p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted">
            {labels.landingMockBoys}
          </p>
          <p className="mt-1 text-2xl font-bold text-text">61</p>
        </div>
        <div className="rounded-xl border border-border bg-surface p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted">
            {labels.landingMockGirls}
          </p>
          <p className="mt-1 text-2xl font-bold text-text">63</p>
        </div>
        <div className="rounded-xl border border-primary/30 bg-primary-light p-3">
          <p className="text-[11px] uppercase tracking-wide text-primary">
            {labels.landingMockTotal}
          </p>
          <p className="mt-1 text-2xl font-bold text-primary">124</p>
        </div>
      </div>

      <div className="space-y-3 px-5 pb-5">
        <div className="overflow-hidden rounded-xl border border-border">
          <div className="grid grid-cols-4 bg-surface px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
            <span>{labels.landingMockClass}</span>
            <span className="text-right">{labels.landingMockBoys}</span>
            <span className="text-right">{labels.landingMockGirls}</span>
            <span className="text-right">{labels.landingMockTotal}</span>
          </div>
          {rows.map((r) => (
            <div
              key={r.c}
              className="grid grid-cols-4 border-t border-border px-4 py-2 text-sm"
            >
              <span className="font-semibold text-text">{r.c}</span>
              <span className="text-right text-muted">{r.b}</span>
              <span className="text-right text-muted">{r.g}</span>
              <span className="text-right font-semibold text-text">{r.b + r.g}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
      whileHover={{ y: -3 }}
      className="rounded-2xl border border-border bg-card p-5 shadow-card"
    >
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-light text-primary">
        <FeatureIcon name={icon} />
      </div>
      <h3 className="mt-4 text-base font-semibold text-text">{title}</h3>
      <p className="mt-1.5 text-sm leading-6 text-muted">{body}</p>
    </motion.div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="group rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-primary-light/20 open:bg-primary-light/30">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-text">
        {q}
        <span
          aria-hidden
          className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-surface text-primary transition-transform group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <p className="mt-2 text-sm leading-6 text-muted">{a}</p>
    </details>
  );
}

export function LandingPage() {
  const [useMalayalam, setUseMalayalam] = useState(false);
  const labels = getLabels(useMalayalam);

  useEffect(() => {
    const lang = new URLSearchParams(window.location.search).get('lang');
    if (lang === 'ml') setUseMalayalam(true);
    if (lang === 'en') setUseMalayalam(false);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('locale-ml', useMalayalam);
    return () => document.documentElement.classList.remove('locale-ml');
  }, [useMalayalam]);

  const features = [
    { id: 'f1', icon: 'edit', title: labels.landingFeature1Title, body: labels.landingFeature1Body },
    { id: 'f2', icon: 'chart', title: labels.landingFeature2Title, body: labels.landingFeature2Body },
    { id: 'f3', icon: 'wifi', title: labels.landingFeature3Title, body: labels.landingFeature3Body },
    { id: 'f4', icon: 'globe', title: labels.landingFeature4Title, body: labels.landingFeature4Body },
    { id: 'f5', icon: 'shield', title: labels.landingFeature5Title, body: labels.landingFeature5Body },
    { id: 'f6', icon: 'device', title: labels.landingFeature6Title, body: labels.landingFeature6Body },
  ];

  const faqs = [
    { id: 'q1', q: labels.landingFaq1Q, a: labels.landingFaq1A },
    { id: 'q2', q: labels.landingFaq2Q, a: labels.landingFaq2A },
    { id: 'q3', q: labels.landingFaq3Q, a: labels.landingFaq3A },
    { id: 'q4', q: labels.landingFaq4Q, a: labels.landingFaq4A },
    { id: 'q5', q: labels.landingFaq5Q, a: labels.landingFaq5A },
    { id: 'q6', q: labels.landingFaq6Q, a: labels.landingFaq6A },
  ];

  const steps = [
    {
      id: 's1',
      k: labels.landingStepOneLabel,
      t: labels.landingStepOneTitle,
      b: labels.landingStepOne,
    },
    {
      id: 's2',
      k: labels.landingStepTwoLabel,
      t: labels.landingStepTwoTitle,
      b: labels.landingStepTwo,
    },
    {
      id: 's3',
      k: labels.landingStepThreeLabel,
      t: labels.landingStepThreeTitle,
      b: labels.landingStepThree,
    },
  ];

  return (
    <div id="top" className="relative min-h-screen overflow-x-hidden bg-surface text-text">
      <SeoHead labels={labels} useMalayalam={useMalayalam} faqs={faqs} />
      {/* decorative background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[640px] bg-gradient-to-b from-primary-light/70 via-surface to-surface dark:from-card dark:via-surface dark:to-surface"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 top-10 -z-10 h-80 w-80 rounded-full bg-primary/25 blur-3xl dark:bg-primary/15"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 top-72 -z-10 h-72 w-72 rounded-full bg-primary-light blur-3xl dark:bg-primary/10"
      />

      <NavBar labels={labels} useMalayalam={useMalayalam} />

      {/* HERO */}
      <main className="mx-auto w-full max-w-6xl px-4 pb-24 pt-28 sm:pt-32">
        <motion.section
          initial="hidden"
          animate="show"
          variants={stagger}
          className="grid items-center gap-10 md:grid-cols-2"
        >
          <div>
            <motion.span
              variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary-light px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {labels.landingHeroBadge}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight text-text sm:text-5xl md:text-6xl"
            >
              {labels.landingTitle}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="mt-4 max-w-xl text-base leading-7 text-muted sm:text-lg"
            >
              {labels.landingSubtitle}
            </motion.p>

            <motion.div variants={fadeUp} className="mt-7 flex flex-wrap items-center gap-3">
              <motion.button
                whileTap={{ scale: 0.97 }}
                type="button"
                onClick={goToLogin}
                className="inline-flex h-12 items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
              >
                {labels.landingLoginCta}
              </motion.button>
              <a
                href={labels.landingAndroidUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={labels.landingAndroidCta}
                className="inline-flex h-12 items-center justify-center gap-2.5 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              >
                <svg
                  aria-hidden
                  width="20"
                  height="22"
                  viewBox="0 0 466 511.98"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  className="flex-none shrink-0"
                >
                  <path
                    fill="#EA4335"
                    fillRule="nonzero"
                    d="M199.9 237.8l-198.5 232.37c7.22,24.57 30.16,41.81 55.8,41.81 11.16,0 20.93,-2.79 29.3,-8.37l0 0 244.16 -139.46 -130.76 -126.35z"
                  />
                  <path
                    fill="#FBBC04"
                    fillRule="nonzero"
                    d="M433.91 205.1l0 0 -104.65 -60 -111.61 110.22 113.01 108.83 104.64 -58.6c18.14,-9.77 30.7,-29.3 30.7,-50.23 -1.4,-20.93 -13.95,-40.46 -32.09,-50.22z"
                  />
                  <path
                    fill="#34A853"
                    fillRule="nonzero"
                    d="M199.42 273.45l129.85 -128.35 -241.37 -136.73c-8.37,-5.58 -19.54,-8.37 -30.7,-8.37 -26.5,0 -50.22,18.14 -55.8,41.86 0,0 0,0 0,0l198.02 231.59z"
                  />
                  <path
                    fill="#4285F4"
                    fillRule="nonzero"
                    d="M1.39 41.86c-1.39,4.18 -1.39,9.77 -1.39,15.34l0 397.64c0,5.57 0,9.76 1.4,15.34l216.27 -214.86 -216.28 -213.46z"
                  />
                </svg>
                <span className="flex flex-col justify-center leading-none text-left">
                  <span className="text-[10px] font-medium uppercase tracking-wide opacity-70">
                    {labels.landingAndroidStoreEyebrow}
                  </span>
                  <span className="mt-0.5 text-sm font-semibold leading-none">
                    {labels.landingAndroidStoreName}
                  </span>
                </span>
              </a>
              <a
                href="/login"
                className="inline-flex h-12 items-center justify-center rounded-xl border border-border bg-card px-5 text-sm font-semibold text-text hover:bg-primary-light/40"
              >
                {labels.landingPwaCta}
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-6 flex flex-wrap gap-2">
              <Chip>{labels.landingChipOffline}</Chip>
              <Chip>{labels.landingChipBilingual}</Chip>
              <Chip>{labels.landingChipMdms}</Chip>
              <Chip>{labels.landingChipFree}</Chip>
            </motion.div>
          </div>

          <HeroMockup labels={labels} />
        </motion.section>

        {/* FEATURES */}
        <motion.section
          id="features"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="mt-24"
        >
          <motion.div variants={fadeUp} className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
              {labels.landingFeaturesTitle}
            </h2>
            <p className="mt-3 text-base leading-7 text-muted">
              {labels.landingFeaturesSubtitle}
            </p>
          </motion.div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <Feature key={f.id} icon={f.icon} title={f.title} body={f.body} />
            ))}
          </div>
        </motion.section>

        {/* ROLES */}
        <motion.section
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="mt-24"
        >
          <motion.div variants={fadeUp} className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
              {labels.landingRolesTitle}
            </h2>
            <p className="mt-3 text-base leading-7 text-muted">
              {labels.landingRolesSubtitle}
            </p>
          </motion.div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              whileHover={{ y: -3 }}
              className="overflow-hidden rounded-2xl border border-border bg-card shadow-card"
            >
              <div className="border-b border-border bg-primary-light/60 px-6 py-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {labels.landingHeadRoleTitle}
                </p>
                <p className="mt-1 text-lg font-bold text-text">
                  {labels.landingHeadRoleBody}
                </p>
              </div>
              <ul className="space-y-2 px-6 py-5 text-sm text-muted">
                <li>• {labels.landingFeature1Title}</li>
                <li>• {labels.landingFeature2Title}</li>
                <li>• {labels.landingFeature3Title}</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.45, ease: 'easeOut', delay: 0.05 }}
              whileHover={{ y: -3 }}
              className="overflow-hidden rounded-2xl border border-border bg-card shadow-card"
            >
              <div className="border-b border-border bg-primary-light/60 px-6 py-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {labels.landingTeacherRoleTitle}
                </p>
                <p className="mt-1 text-lg font-bold text-text">
                  {labels.landingTeacherRoleBody}
                </p>
              </div>
              <ul className="space-y-2 px-6 py-5 text-sm text-muted">
                <li>• {labels.landingFeature1Title}</li>
                <li>• {labels.landingFeature3Title}</li>
                <li>• {labels.landingFeature6Title}</li>
              </ul>
            </motion.div>
          </div>
        </motion.section>

        {/* USER GUIDE PDF */}
        <motion.section
          id="guide"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="mt-24"
        >
          <motion.div
            variants={fadeUp}
            className="overflow-hidden rounded-3xl border border-primary/25 bg-gradient-to-br from-primary-light/80 via-card to-card p-8 shadow-card dark:from-primary/15 dark:via-card sm:p-10"
          >
            <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
              <div className="flex gap-4 md:max-w-2xl">
                <div
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-sm"
                  aria-hidden
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {labels.landingHelpPdfBadge}
                  </p>
                  <h2 className="mt-1 text-2xl font-bold tracking-tight text-text sm:text-3xl">
                    {labels.landingHelpPdfTitle}
                  </h2>
                  <p className="mt-2 text-sm leading-7 text-muted sm:text-base">
                    {labels.landingHelpPdfBody}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 md:shrink-0 md:flex-col lg:flex-row">
                <a
                  href={HELP_PDF_URL}
                  download="NMHelper-User-Guide.pdf"
                  className="inline-flex h-12 min-w-[11rem] items-center justify-center rounded-xl bg-primary px-6 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
                >
                  {labels.landingHelpPdfCta}
                </a>
                <a
                  href={HELP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-12 min-w-[11rem] items-center justify-center rounded-xl border border-border bg-card px-6 text-sm font-semibold text-text hover:bg-surface"
                >
                  {labels.landingHelpPdfOnline}
                </a>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* HOW IT WORKS */}
        <motion.section
          id="how"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="mt-24"
        >
          <motion.div variants={fadeUp} className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
              {labels.landingHowTitle}
            </h2>
          </motion.div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {steps.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, ease: 'easeOut', delay: i * 0.06 }}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <p className="text-xs font-bold uppercase tracking-widest text-primary">
                  {s.k}
                </p>
                <p className="mt-2 text-lg font-semibold text-text">{s.t}</p>
                <p className="mt-2 text-sm leading-6 text-muted">{s.b}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section
          id="faq"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="mt-24"
        >
          <motion.div variants={fadeUp} className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">
              {labels.landingFaqTitle}
            </h2>
          </motion.div>

          <div className="mx-auto mt-8 grid max-w-3xl gap-3">
            {faqs.map((f, i) => (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, ease: 'easeOut', delay: i * 0.04 }}
              >
                <FaqItem q={f.q} a={f.a} />
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* FINAL CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mt-24 overflow-hidden rounded-3xl border border-primary/20 bg-primary px-8 py-12 text-white shadow-card"
        >
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {labels.landingCtaBannerTitle}
              </h2>
              <p className="mt-1 max-w-xl text-sm text-white/85 sm:text-base">
                {labels.landingCtaBannerBody}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={goToLogin}
                className="rounded-xl bg-white px-5 py-3 text-sm font-semibold text-primary shadow-sm hover:bg-white/95"
              >
                {labels.landingLoginCta}
              </button>
              <a
                href={SUPPORT_WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-white/40 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
              >
                {labels.contactWhatsApp}
              </a>
            </div>
          </div>
        </motion.section>

        {/* CONTACT */}
        <section className="mt-16 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h3 className="text-lg font-semibold text-text">
              {labels.landingContactTitle}
            </h3>
            <p className="mt-2 text-sm leading-6 text-muted">
              {labels.landingContactBody}
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={SUPPORT_MAILTO}
                className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold hover:bg-primary-light/40"
              >
                {labels.contactEmail}
              </a>
              <a
                href={SUPPORT_WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold hover:bg-primary-light/40"
              >
                {labels.contactWhatsApp}
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h3 className="text-lg font-semibold text-text">NMhelper</h3>
            <p className="mt-2 text-sm leading-6 text-muted">
              {labels.landingFooterTagline}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {labels.landingFooterTerms}
              </a>
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {labels.landingFooterPrivacy}
              </a>
              <a
                href={HELP_PDF_URL}
                download="NMHelper-User-Guide.pdf"
                className="text-primary hover:underline"
              >
                {labels.landingFooterPdf}
              </a>
              <a
                href={HELP_URL}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {labels.landingFooterHelp}
              </a>
              <a
                href={DELETE_DATA_URL}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {labels.landingFooterDeleteData}
              </a>
              <a
                href={TESTER_URL}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {labels.landingFooterTester}
              </a>
              <a
                href={GITHUB_PROFILE_URL}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {labels.landingFooterGithub}
              </a>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
                {SUPPORT_EMAIL}
              </a>
              <a href={`mailto:${SUPPORT_EMAIL_2}`} className="text-primary hover:underline">
                {SUPPORT_EMAIL_2}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-card/70 py-8">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-4 text-sm text-muted md:flex-row">
          <div className="flex items-center gap-2">
            <AppLogoIcon className="h-6 w-6 object-contain" />
            <span className="font-semibold text-text">NMhelper</span>
          </div>
          <p>
            Copyright &copy; 2026 Nishal K. NMhelper. {labels.landingFooterRights}
          </p>
        </div>
      </footer>
    </div>
  );
}
